import type { EncodeRequest, EncodeResult } from "./codec";
import type { WorkerResponse } from "./codec.worker";

/**
 * Runs one encode in a Worker, falling back to the main thread.
 *
 * The pixels are cloned into the Worker rather than transferred: transferring
 * would detach the buffer on this side, and the fallback below needs the data
 * intact when a Worker fails to start (module Workers are unavailable on older
 * Safari) or dies mid-encode. The copy costs tens of milliseconds against an
 * encode measured in hundreds — worth it to keep the fallback real.
 *
 * The result travels the other way as a transfer, since nothing in the Worker
 * needs it afterwards.
 */
const encodeInWorker = (request: EncodeRequest): Promise<EncodeResult> =>
  new Promise((resolve, reject) => {
    const worker = new Worker(new URL("./codec.worker.ts", import.meta.url), {
      type: "module",
    });

    const finish = <T>(settle: (value: T) => void) => (value: T) => {
      worker.terminate();
      settle(value);
    };

    worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
      if (data.ok) {
        finish(resolve)({ buffer: data.buffer, mimeType: data.mimeType });
        return;
      }

      finish(reject)(new Error(data.error));
    };

    worker.onerror = ({ message }) => finish(reject)(new Error(message));

    worker.postMessage(request);
  });

export const runEncode = async (
  request: EncodeRequest
): Promise<EncodeResult> => {
  try {
    return await encodeInWorker(request);
  } catch {
    // Imported here rather than at the top so the WASM codecs stay out of the
    // main bundle: on the happy path they are only ever loaded by the Worker.
    const { encodeToTarget } = await import("./codec");

    return encodeToTarget(request);
  }
};
